/**
 * localStorage persistence for the in-progress new-lead form.
 *
 * Why this exists: this PWA has no offline write queue. A rep inside a basement
 * kitchen or a market interior with no signal can fill the whole form, tap Save,
 * get a failed POST, background the app to take a call — and lose everything,
 * which is the exact failure this feature was built to eliminate.
 *
 * This does not make capture work offline. It makes a failed capture recoverable:
 * reopen the form, the draft is offered back, tap Save again once there is signal.
 * A real outbox (IndexedDB + Background Sync + server idempotency keys) is the
 * proper fix and is deliberately out of scope.
 *
 * Photos are File objects and are not serialisable, so they are not part of the
 * draft — the rep re-picks them. Everything typed is preserved.
 */

const DRAFT_KEY = 'leads:draft:v1';

export interface LeadDraft {
    values: Record<string, unknown>;
    savedAt: string;
}

export const saveLeadDraft = (values: Record<string, unknown>): void => {
    try {
        // Don't offer to restore a form the rep has barely started.
        const meaningful = Object.entries(values).some(
            ([key, value]) =>
                key !== 'source' && typeof value === 'string' && value.trim().length > 0
        );
        if (!meaningful) return;

        const draft: LeadDraft = { values, savedAt: new Date().toISOString() };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
        // Private mode / quota. Losing the draft is bad but must never break the form.
    }
};

export const loadLeadDraft = (): LeadDraft | null => {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as LeadDraft;
        if (!parsed?.values) return null;

        // A week-old draft is noise, not a rescue.
        const age = Date.now() - new Date(parsed.savedAt).getTime();
        if (Number.isNaN(age) || age > 7 * 24 * 60 * 60 * 1000) {
            clearLeadDraft();
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

export const clearLeadDraft = (): void => {
    try {
        localStorage.removeItem(DRAFT_KEY);
    } catch {
        /* ignore */
    }
};
